import React from 'react';
import Modal from './modal';
import { buildImportSummaryLines, formatImportErrorLine } from '../utils/import-result-helpers';

// Renders the {status, summary, errors, alerts} shape produced by xpens-api's
// shared CSV import foundation (src/utils/csv_import.py). Intended for every
// admin import screen (departments, imputations, users, suppliers) once
// XPEN-3145-003/006/009 adopt it.
//
// The first line of the body states the outcome, read from `result.status`
// (XPEN-3320). Before that the modal showed counters, a red "Row errors" block
// and an amber "Warnings" block, but never said whether the import had
// worked — so a near-perfect import was reported as broken. See XPEN-3320 for
// the incident. An unknown or absent status renders no outcome line at all
// rather than guessing.
//
// A Map, not an object literal, on purpose. `status` arrives on an API response
// body, and an object lookup walks Object.prototype — so a status of
// "constructor" or "__proto__" would return a truthy value, pass the guard
// below, and then hand `undefined` to Translator.read, which does
// key.split('.') and throws during render. There is no error boundary in this
// app, so that blanks the page. Map.get has no such inherited keys.
const OUTCOME_LINES = new Map([
    ['success', { key: 'import_result.outcome_success', fallback: 'Import completed.', className: 'text-success' }],
    ['partial_success', {
        key: 'import_result.outcome_partial_success',
        // "could not be imported", not "skipped": partial_success is set when
        // rows_failed > 0, and the red "Row errors" block listing them renders
        // directly below this line.
        fallback: 'Import completed — some rows could not be imported.',
        className: 'text-warning',
    }],
    ['failed', { key: 'import_result.outcome_failed', fallback: 'Import failed.', className: 'text-danger' }],
]);

export default function ImportResultModal({ data, result, summarySpec, onClose }) {
    if (!result) return null;

    // Absent or unrecognised status → no outcome line. Callers that predate the
    // status field, or a status this build does not know, must still render the
    // rest of the modal exactly as before.
    const outcome = OUTCOME_LINES.get(result.status);

    const errorLines = (result.errors ?? []).map((e) => formatImportErrorLine(e, data.t)).filter(Boolean);
    const alertLines = (result.alerts ?? []).map((a) => formatImportErrorLine(a, data.t)).filter(Boolean);

    return (
        <Modal
            title={data.t.read('import_result.title') || 'Import Results'}
            okText="OK"
            okAction={onClose}
        >
            <div>
                {outcome && (
                    <p className={`font-weight-bold ${outcome.className} mb-2`}>
                        {data.t.read(outcome.key) || outcome.fallback}
                    </p>
                )}
                <p style={{ whiteSpace: 'pre-line' }} className="mb-3">
                    {buildImportSummaryLines(result, data.t, summarySpec ?? [])}
                </p>
                {errorLines.length > 0 && (
                    <div className="mt-2">
                        <div className="font-weight-bold text-danger mb-1">
                            {data.t.read('import_result.errors_title') || 'Row errors'}
                        </div>
                        <ul className="text-danger small mb-0 pl-3">
                            {errorLines.map((line, i) => (
                                <li key={i}>{line}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {alertLines.length > 0 && (
                    <div className="mt-2">
                        <div className="font-weight-bold text-warning mb-1">
                            {data.t.read('import_result.alerts_title') || 'Warnings'}
                        </div>
                        <ul className="text-warning small mb-0 pl-3">
                            {alertLines.map((line, i) => (
                                <li key={i}>{line}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </Modal>
    );
}
